import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CryptoWidget = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [selectedTab, setSelectedTab] = useState('top10');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    en: {
      title: "Live Crypto Market",
      subtitle: "Real-time cryptocurrency tracking and AOGX token performance",
      tabs: {
        top10: "Top 10",
        stablecoins: "Stablecoins",
        aogx: "AOGX Token"
      },
      viewDashboard: "View Full Dashboard",
      marketCap: "Market Cap",
      change24h: "24h Change",
      price: "Price"
    },
    de: {
      title: "Live Krypto-Markt",
      subtitle: "Echtzeit-Kryptowährungsverfolgung und AOGX-Token-Performance",
      tabs: {
        top10: "Top 10",
        stablecoins: "Stablecoins",
        aogx: "AOGX Token"
      },
      viewDashboard: "Vollständiges Dashboard anzeigen",
      marketCap: "Marktkapitalisierung",
      change24h: "24h Änderung",
      price: "Preis"
    },
    fr: {
      title: "Marché Crypto en Direct",
      subtitle: "Suivi en temps réel des cryptomonnaies et performance du token AOGX",
      tabs: {
        top10: "Top 10",
        stablecoins: "Stablecoins",
        aogx: "Token AOGX"
      },
      viewDashboard: "Voir le Tableau de Bord Complet",
      marketCap: "Capitalisation",
      change24h: "Changement 24h",
      price: "Prix"
    },
    es: {
      title: "Mercado Crypto en Vivo",
      subtitle: "Seguimiento en tiempo real de criptomonedas y rendimiento del token AOGX",
      tabs: {
        top10: "Top 10",
        stablecoins: "Stablecoins",
        aogx: "Token AOGX"
      },
      viewDashboard: "Ver Dashboard Completo",
      marketCap: "Cap. de Mercado",
      change24h: "Cambio 24h",
      price: "Precio"
    }
  };

  const mockCryptoData = {
    top10: [
      { symbol: "BTC", name: "Bitcoin", price: "$43,250.00", change: "+2.34%", isPositive: true, marketCap: "$847.2B" },
      { symbol: "ETH", name: "Ethereum", price: "$2,650.00", change: "+1.87%", isPositive: true, marketCap: "$318.5B" },
      { symbol: "BNB", name: "BNB", price: "$315.20", change: "-0.45%", isPositive: false, marketCap: "$48.7B" },
      { symbol: "XRP", name: "XRP", price: "$0.6234", change: "+3.21%", isPositive: true, marketCap: "$33.8B" },
      { symbol: "SOL", name: "Solana", price: "$98.45", change: "+5.67%", isPositive: true, marketCap: "$42.1B" }
    ],
    stablecoins: [
      { symbol: "USDT", name: "Tether", price: "$1.0001", change: "+0.01%", isPositive: true, marketCap: "$91.2B" },
      { symbol: "USDC", name: "USD Coin", price: "$0.9998", change: "-0.02%", isPositive: false, marketCap: "$25.8B" },
      { symbol: "BUSD", name: "Binance USD", price: "$1.0000", change: "0.00%", isPositive: true, marketCap: "$5.2B" },
      { symbol: "DAI", name: "Dai", price: "$0.9999", change: "+0.01%", isPositive: true, marketCap: "$4.1B" }
    ],
    aogx: [
      { 
        symbol: "AOGX", 
        name: "AOGX Token", 
        price: "$12.45", 
        change: "+8.92%", 
        isPositive: true, 
        marketCap: "$124.5M",
        volume24h: "$2.3M",
        totalSupply: "10M AOGX"
      }
    ]
  };

  const currentContent = content?.[currentLanguage];
  const currentData = mockCryptoData?.[selectedTab];

  return (
    <section className="py-16 bg-card">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12 scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {currentContent?.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {currentContent?.subtitle}
          </p>
        </div>

        <div className="bg-background rounded-brand shadow-brand border border-border overflow-hidden scroll-reveal">
          {/* Tab Navigation */}
          <div className="flex border-b border-border">
            {Object.entries(currentContent?.tabs)?.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setSelectedTab(key)}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-brand ${
                  selectedTab === key
                    ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Crypto Data Display */}
          <div className="p-6">
            {selectedTab === 'aogx' ? (
              /* AOGX Token Special Display */
              (<div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-brand p-6 border border-primary/20">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-xl">A</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-foreground">AOGX Token</h3>
                        <p className="text-muted-foreground">REXFEL Portfolio Asset</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-background rounded-brand-sm p-4">
                        <div className="text-sm text-muted-foreground">{currentContent?.price}</div>
                        <div className="text-2xl font-bold text-foreground">$12.45</div>
                      </div>
                      <div className="bg-background rounded-brand-sm p-4">
                        <div className="text-sm text-muted-foreground">{currentContent?.change24h}</div>
                        <div className="text-2xl font-bold text-success">+8.92%</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-muted-foreground">{currentContent?.marketCap}</span>
                      <span className="font-medium text-foreground">$124.5M</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-muted-foreground">24h Volume</span>
                      <span className="font-medium text-foreground">$2.3M</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-muted-foreground">Total Supply</span>
                      <span className="font-medium text-foreground">10M AOGX</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-muted-foreground">Circulating Supply</span>
                      <span className="font-medium text-foreground">7.2M AOGX</span>
                    </div>
                  </div>
                </div>
              </div>)
            ) : (
              /* Regular Crypto List */
              (<div className="space-y-3">
                {currentData?.map((crypto, index) => (
                  <div 
                    key={crypto?.symbol}
                    className={`flex items-center justify-between p-4 rounded-brand-sm hover:bg-muted transition-brand hover-lift stagger-${index + 1}`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full flex items-center justify-center">
                        <span className="font-bold text-sm text-primary">{crypto?.symbol?.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{crypto?.name}</div>
                        <div className="text-sm text-muted-foreground">{crypto?.symbol}</div>
                      </div>
                    </div>

                    <div className="text-right space-y-1">
                      <div className="font-bold text-foreground">{crypto?.price}</div>
                      <div className={`text-sm font-medium ${
                        crypto?.isPositive ? 'text-success' : 'text-destructive'
                      }`}>
                        {crypto?.change}
                      </div>
                    </div>

                    <div className="text-right hidden md:block">
                      <div className="text-sm text-muted-foreground">{currentContent?.marketCap}</div>
                      <div className="font-medium text-foreground">{crypto?.marketCap}</div>
                    </div>
                  </div>
                ))}
              </div>)
            )}
          </div>

          {/* View Dashboard CTA */}
          <div className="px-6 py-4 bg-muted border-t border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Activity" size={16} />
                <span>Live data updates every 30 seconds</span>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                iconPosition="right"
              >
                <Link to="/crypto-dashboard">
                  {currentContent?.viewDashboard}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CryptoWidget;